import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, FormGroup, Label, Input, Table } from 'reactstrap';
import { ExportTemplateEditor } from '../controls/ExportTemplateEditor';

export const AdminSettings = () => {
    const [settings, setSettings] = useState({ defaultLatitude: 0, defaultLongitude: 0, gridCorrections: [], exportTemplate: null, angleDisplay: null });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [newCorrection, setNewCorrection] = useState({ appliesFrom: '', correction: 0 });
    const [logoUrl, setLogoUrl] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoError, setLogoError] = useState('');
    const [logoSuccess, setLogoSuccess] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        checkAdminAccess();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only admin check

    const checkAdminAccess = async () => {
        try {
            const response = await fetch('api/identity/getuser');
            const data = await response.json();
            if (data.roles && data.roles.some(r => r.toLowerCase() === 'admin')) {
                setIsAdmin(true);
                loadSettings();
                loadLogo();
            } else {
                navigate('/');
            }
        } catch (err) {
            navigate('/');
        }
    };

    const loadSettings = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch('api/identity/customersettings');
            if (response.ok) {
                const data = await response.json();
                // Keep the WHOLE response, not the three fields this page edits.
                // The server merges a patch now (Services/SettingsMerge.cs), but
                // round-tripping everything means an older cached bundle cannot
                // drop a field either.
                setSettings({
                    ...data,
                    defaultLatitude: data.defaultLatitude ?? 0,
                    defaultLongitude: data.defaultLongitude ?? 0,
                    gridCorrections: data.gridCorrections ?? []
                });
            } else {
                setError('Failed to load settings.');
            }
        } catch (err) {
            setError('Failed to load settings.');
        }
        setLoading(false);
    };

    const loadLogo = async () => {
        try {
            const response = await fetch('api/projects');
            if (response.ok) {
                const data = await response.json();
                setLogoUrl(data.logoUrl || '');
            }
        } catch (err) {
            // Non-fatal: the logo section just shows "no logo" if this fails.
        }
    };

    const handleLogoFileChange = (e) => {
        setLogoFile(e.target.files[0] || null);
        setLogoError('');
        setLogoSuccess('');
    };

    const handleLogoUpload = async () => {
        if (!logoFile) {
            setLogoError('Please choose an image file first.');
            return;
        }
        setLogoError('');
        setLogoSuccess('');
        setLogoUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', logoFile);
            const response = await fetch('api/projects/uploadlogo', {
                method: 'POST',
                body: formData
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Failed to upload logo.');
            }
            const data = await response.json();
            setLogoUrl(data.logoUrl || '');
            setLogoFile(null);
            setLogoSuccess('Logo updated successfully. It will appear on the projects list next time it loads.');
        } catch (err) {
            setLogoError(err.message);
        }
        setLogoUploading(false);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setSaving(true);

        try {
            const response = await fetch('api/identity/admin/settings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings)
            });
            const data = await response.json();

            if (data.success) {
                setSuccess('Settings saved successfully.');
            } else {
                setError(data.message || 'Failed to save settings.');
            }
        } catch (err) {
            setError('Failed to save settings.');
        }
        setSaving(false);
    };

    const handleLatitudeChange = (e) => {
        const value = e.target.value;
        setSettings({ ...settings, defaultLatitude: value === '' ? 0 : parseFloat(value) || 0 });
    };

    const handleLongitudeChange = (e) => {
        const value = e.target.value;
        setSettings({ ...settings, defaultLongitude: value === '' ? 0 : parseFloat(value) || 0 });
    };

    const handleAddCorrection = (e) => {
        e.preventDefault();
        if (!newCorrection.appliesFrom) {
            setError('Please select a date for the grid correction.');
            return;
        }
        // This value is added to every exported azimuth, so a typo lands
        // silently in the customer's own database. A correction is a bearing
        // offset: outside +/-180 it cannot be one, and a whole-number entry an
        // order of magnitude out (19 for 1.9) is worth a second look.
        const value = newCorrection.correction;
        if (typeof value !== 'number' || isNaN(value)) {
            setError('Please enter a numeric grid correction.');
            return;
        }
        if (value < -180 || value > 180) {
            setError('A grid correction must be between -180 and 180 degrees.');
            return;
        }
        if (Math.abs(value) > 45 && !window.confirm(
            `${value}° is a very large grid correction and will be added to every exported `
            + 'azimuth. Is that definitely right?')) {
            return;
        }
        setError('');

        const correction = {
            id: crypto.randomUUID(),
            appliesFrom: newCorrection.appliesFrom,
            correction: newCorrection.correction
        };

        const updatedCorrections = [...settings.gridCorrections, correction];
        setSettings({ ...settings, gridCorrections: updatedCorrections });
        setNewCorrection({ appliesFrom: '', correction: 0 });
    };

    const handleDeleteCorrection = (id) => {
        if (!window.confirm('Are you sure you want to delete this grid correction?')) {
            return;
        }
        const updatedCorrections = settings.gridCorrections.filter(c => c.id !== id);
        setSettings({ ...settings, gridCorrections: updatedCorrections });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const getSortedCorrections = () => {
        return [...settings.gridCorrections].sort((a, b) =>
            new Date(b.appliesFrom) - new Date(a.appliesFrom)
        );
    };

    if (!isAdmin) {
        return <div className="container" style={{ marginTop: 10 }}>Checking access...</div>;
    }

    return (
        <div className="container" style={{ marginTop: 10 }}>
            <div className="row">
                <div className="col-12 col-md-6">
                    <h3>Customer Settings</h3>
                    <p className="text-muted">Configure default settings for your organization.</p>

                    {error && <div className="alert alert-danger">{error}</div>}
                    {success && <div className="alert alert-success">{success}</div>}

                    {loading ? (
                        <p>Loading settings...</p>
                    ) : (
                        <>
                            <Form onSubmit={handleSave}>
                                <FormGroup>
                                    <Label for="defaultLatitude">Default Latitude</Label>
                                    <Input
                                        type="number"
                                        id="defaultLatitude"
                                        step="any"
                                        value={settings.defaultLatitude}
                                        onChange={handleLatitudeChange}
                                        placeholder="Enter default latitude"
                                    />
                                    <small className="text-muted">Used as the default latitude when creating new drillholes.</small>
                                </FormGroup>
                                <FormGroup>
                                    <Label for="defaultLongitude">Default Longitude</Label>
                                    <Input
                                        type="number"
                                        id="defaultLongitude"
                                        step="any"
                                        value={settings.defaultLongitude}
                                        onChange={handleLongitudeChange}
                                        placeholder="Enter default longitude"
                                    />
                                    <small className="text-muted">Used as the default longitude when creating new drillholes.</small>
                                </FormGroup>

                                <hr />

                                <h5>Grid Corrections</h5>
                                <p className="text-muted">Manage grid corrections that apply from specific dates.</p>

                                {settings.gridCorrections.length > 0 ? (
                                    <Table striped size="sm">
                                        <thead>
                                            <tr>
                                                <th>Applies From</th>
                                                <th>Correction</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getSortedCorrections().map(correction => (
                                                <tr key={correction.id}>
                                                    <td>{formatDate(correction.appliesFrom)}</td>
                                                    <td>{correction.correction}</td>
                                                    <td>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteCorrection(correction.id)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <p className="text-muted"><em>No grid corrections configured.</em></p>
                                )}

                                <div className="card mb-3">
                                    <div className="card-body">
                                        <h6 className="card-title">Add Grid Correction</h6>
                                        <div className="row">
                                            <div className="col-md-5">
                                                <FormGroup>
                                                    <Label for="correctionDate">Applies From</Label>
                                                    <Input
                                                        type="date"
                                                        id="correctionDate"
                                                        value={newCorrection.appliesFrom}
                                                        onChange={(e) => setNewCorrection({ ...newCorrection, appliesFrom: e.target.value })}
                                                    />
                                                </FormGroup>
                                            </div>
                                            <div className="col-md-4">
                                                <FormGroup>
                                                    <Label for="correctionValue">Correction</Label>
                                                    <Input
                                                        type="number"
                                                        id="correctionValue"
                                                        step="any"
                                                        value={newCorrection.correction}
                                                        onChange={(e) => setNewCorrection({ ...newCorrection, correction: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                                                    />
                                                </FormGroup>
                                            </div>
                                            <div className="col-md-3 d-flex align-items-end">
                                                <FormGroup>
                                                    <Button color="secondary" onClick={handleAddCorrection}>
                                                        Add
                                                    </Button>
                                                </FormGroup>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <hr />

                                <ExportTemplateEditor
                                    template={settings.exportTemplate}
                                    onChange={next => setSettings({ ...settings, exportTemplate: next })}
                                    angleDisplay={settings.angleDisplay}
                                    onAngleDisplayChange={next => setSettings({ ...settings, angleDisplay: next })} />

                                <Button color="primary" type="submit" className="mt-3" disabled={saving}>
                                    {saving ? 'Saving...' : 'Save Settings'}
                                </Button>
                            </Form>

                            <hr />

                            <h5>Customer Logo</h5>
                            <p className="text-muted">This logo appears at the top of the projects list.</p>

                            {logoError && <div className="alert alert-danger">{logoError}</div>}
                            {logoSuccess && <div className="alert alert-success">{logoSuccess}</div>}

                            {logoUrl ? (
                                <div className="mb-2">
                                    <img
                                        src={logoUrl}
                                        alt="Customer logo"
                                        style={{ maxWidth: 256, maxHeight: 128, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 4, padding: 4 }}
                                    />
                                </div>
                            ) : (
                                <p className="text-muted"><em>No logo uploaded yet.</em></p>
                            )}

                            <FormGroup>
                                <Label for="logoFile">Upload new logo</Label>
                                <Input
                                    type="file"
                                    id="logoFile"
                                    accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                                    onChange={handleLogoFileChange}
                                />
                                <small className="text-muted">PNG, JPG, GIF, SVG or WEBP. A wide logo with a transparent background works best.</small>
                            </FormGroup>
                            <Button color="primary" onClick={handleLogoUpload} disabled={logoUploading || !logoFile}>
                                {logoUploading ? 'Uploading...' : 'Upload Logo'}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
